import { useState, useRef, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    max-height: 500px;
    transform: translateY(0);
  }
`;

const SummaryContainer = styled.div<{ $position?: 'fixed' | 'relative' }>`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  animation: ${fadeIn} 0.4s ease-out;
  overflow: hidden;
  position: ${({ $position = 'relative' }) => $position};
  z-index: 1000;
  
  /* Enhanced mobile-first styling with better spacing */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
    margin: 0 ${({ theme }) => theme.spacing.mobile.gutter} ${({ theme }) => theme.spacing.mobile.lg};
    
    /* Enhanced mobile positioning */
    ${({ $position }) => $position === 'fixed' && css`
      position: sticky;
      top: ${({ theme }) => theme.spacing.mobile.md};
      margin-bottom: ${({ theme }) => theme.spacing.mobile.xl};
    `}
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    border-radius: ${({ theme }) => theme.borderRadius.xl};
    box-shadow: ${({ theme }) => theme.shadows.xl};
    margin: 0 ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
    
    /* Sticky positioning on larger mobile for better visibility */
    ${({ $position }) => $position === 'fixed' && css`
      position: sticky;
      top: ${({ theme }) => theme.spacing.lg};
    `}
  }
  
  /* Improved touch interactions for all mobile devices */
  @media (hover: none) and (pointer: coarse) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    
    /* Enhanced border for better definition on mobile */
    border: 1px solid ${({ theme }) => theme.colors.mobile.border};
    
    /* Better shadow for mobile depth perception */
    box-shadow: ${({ theme }) => theme.shadows.gallery.artwork.frame};
  }
  
  /* Desktop improvements */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
    box-shadow: ${({ theme }) => theme.shadows.gallery.soft};
    
    /* Enhanced hover state on desktop */
    transition: box-shadow ${({ theme }) => theme.transitions.gallery.hover};
    
    &:hover {
      box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
    }
  }
`;

const SummaryHeader = styled.div<{ $isSuccess: boolean; $hasErrors: boolean }>`
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
  background: ${({ theme, $isSuccess, $hasErrors }) => {
    if ($isSuccess && !$hasErrors) return `linear-gradient(135deg, ${theme.colors.success}15, ${theme.colors.success}25)`;
    if ($hasErrors && $isSuccess) return `linear-gradient(135deg, ${theme.colors.warning}15, ${theme.colors.warning}25)`;
    return `linear-gradient(135deg, ${theme.colors.error}15, ${theme.colors.error}25)`;
  }};
  border-bottom: 2px solid ${({ theme, $isSuccess, $hasErrors }) => {
    if ($isSuccess && !$hasErrors) return theme.colors.success + '40';
    if ($hasErrors && $isSuccess) return theme.colors.warning + '40';
    return theme.colors.error + '40';
  }};

  /* Mobile-first responsive padding */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.mobile.md} ${({ theme }) => theme.spacing.mobile.padding};
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  }
  
  /* Enhanced visual hierarchy on larger screens */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: ${({ theme }) => theme.spacing.gallery.sm} ${({ theme }) => theme.spacing.gallery.md};
    
    /* Subtle enhancement for desktop */
    border-bottom-width: 3px;
  }
`;

const SummaryTitle = styled.h3<{ $isSuccess: boolean; $hasErrors: boolean }>`
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.heading};
  font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  line-height: ${({ theme }) => theme.typography.lineHeight.snug};
  color: ${({ theme, $isSuccess, $hasErrors }) => {
    if ($isSuccess && !$hasErrors) return theme.colors.success;
    if ($hasErrors && $isSuccess) return theme.colors.warning;
    return theme.colors.error;
  }};

  &::before {
    content: '${({ $isSuccess, $hasErrors }) => {
      if ($isSuccess && !$hasErrors) return '✅';
      if ($hasErrors && $isSuccess) return '⚠️';
      return '❌';
    }}';
    font-size: 20px;
    flex-shrink: 0;
  }

  /* Mobile-first typography scaling */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme }) => theme.typography.fontSize.mobile.heading};
    gap: ${({ theme }) => theme.spacing.mobile.xs};
    
    &::before {
      font-size: 16px;
    }
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    
    &::before {
      font-size: 18px;
    }
  }
  
  /* Enhanced desktop typography */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.section};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.bold};
    
    &::before {
      font-size: 22px;
    }
  }
`;

const SummaryStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  /* Mobile-first responsive layout */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.mobile.sm};
    flex-direction: column;
    align-items: stretch;
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    gap: ${({ theme }) => theme.spacing.md};
    flex-direction: row;
    justify-content: space-between;
  }
  
  /* Enhanced desktop layout */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    gap: ${({ theme }) => theme.spacing.gallery.sm};
    margin-bottom: ${({ theme }) => theme.spacing.gallery.tiny};
  }
`;

const StatItem = styled.div<{ $type: 'success' | 'error' | 'total' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  min-width: 0; /* Allow text truncation if needed */
  
  .icon {
    width: 16px;
    height: 16px;
    border-radius: ${({ theme }) => theme.borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme, $type }) => {
      if ($type === 'success') return theme.colors.success;
      if ($type === 'error') return theme.colors.error;
      return theme.colors.gray.medium;
    }};
    flex-shrink: 0;
  }
  
  .label {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.mobile.textSecondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  }
  
  .count {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.bold};
    color: ${({ theme, $type }) => {
      if ($type === 'success') return theme.colors.success;
      if ($type === 'error') return theme.colors.error;
      return theme.colors.mobile.text;
    }};
    margin-left: auto; /* Push count to the right on mobile */
  }

  /* Mobile-first responsive adjustments */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.mobile.xs} ${({ theme }) => theme.spacing.mobile.sm};
    background: ${({ theme }) => theme.colors.mobile.surface};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.subtle};
    border: 1px solid ${({ theme }) => theme.colors.mobile.border};
    justify-content: space-between;
    
    .icon {
      width: 18px;
      height: 18px;
      font-size: 11px;
    }
    
    .count {
      font-size: ${({ theme }) => theme.typography.fontSize.mobile.body};
      margin-left: ${({ theme }) => theme.spacing.mobile.xs};
    }
    
    .label {
      font-size: ${({ theme }) => theme.typography.fontSize.mobile.caption};
      flex: 1;
      margin-left: ${({ theme }) => theme.spacing.mobile.xs};
    }
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    .count {
      font-size: ${({ theme }) => theme.typography.fontSize.base};
    }
    
    .label {
      font-size: ${({ theme }) => theme.typography.fontSize.xs};
    }
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    .icon {
      width: 18px;
      height: 18px;
      font-size: 12px;
    }
    
    .count {
      font-size: ${({ theme }) => theme.typography.fontSize.gallery.metadata};
    }
    
    .label {
      font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
    }
  }
`;

const ToggleButton = styled.button<{ $expanded: boolean }>`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all ${({ theme }) => theme.transitions.fast};
  width: 100%;
  justify-content: space-between;
  
  &:hover {
    color: ${({ theme }) => theme.colors.secondary};
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
  
  .chevron {
    transition: transform ${({ theme }) => theme.transitions.fast};
    transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.sm};
    min-height: 48px; /* Enhanced touch target */
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    background: ${({ theme }) => theme.colors.gray.light}20;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    margin-top: ${({ theme }) => theme.spacing.sm};
    
    &:active {
      background: ${({ theme }) => theme.colors.gray.light}40;
      transform: scale(0.98);
    }
    
    .chevron {
      font-size: 18px;
      width: 28px;
      height: 28px;
    }
  }
`;

const ErrorDetails = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.normal};
  background: ${({ theme }) => theme.colors.background};
  
  ${({ $expanded }) => $expanded ? css`
    max-height: 600px;
    opacity: 1;
    animation: ${slideDown} 0.3s ease-out;
  ` : css`
    max-height: 0;
    opacity: 0;
  `}
  
  /* Enhanced mobile styling */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    /* Ensure proper scrolling on mobile */
    ${({ $expanded }) => $expanded && css`
      max-height: 50vh;
      overflow-y: auto;
      /* Smooth scrolling for mobile */
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
    `}
  }
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  }
`;

const ErrorItem = styled.li`
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.white};
  border-left: 4px solid ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.md};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.md};
    
    /* Enhanced touch feedback */
    &:active {
      transform: scale(0.99);
      box-shadow: ${({ theme }) => theme.shadows.sm};
    }
  }
  
  /* Enhanced mobile touch styling */
  @media (hover: none) and (pointer: coarse) {
    border: 1px solid ${({ theme }) => theme.colors.error}30;
    border-left: 4px solid ${({ theme }) => theme.colors.error};
  }
`;

const FileName = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: 1.5;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }
`;

const ActionButtons = styled.div`
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xl};
  border-top: 1px solid ${({ theme }) => theme.colors.gray.light};
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  background: ${({ theme }) => theme.colors.background}80;
  backdrop-filter: blur(4px);

  /* Mobile-first responsive layout */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.mobile.md};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.mobile.sm};
    
    /* Enhanced sticky positioning for mobile */
    position: sticky;
    bottom: 0;
    background: ${({ theme }) => theme.colors.mobile.surface};
    border-top: 2px solid ${({ theme }) => theme.colors.mobile.border};
    box-shadow: 0 -6px 24px ${({ theme }) => theme.colors.mobile.overlay};
    backdrop-filter: blur(8px);
    
    /* Safe area support for iPhone X and newer */
    padding-bottom: max(${({ theme }) => theme.spacing.mobile.md}, env(safe-area-inset-bottom));
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.lg};
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing.sm};
    
    /* Sticky action buttons on larger mobile */
    position: sticky;
    bottom: 0;
    background: ${({ theme }) => theme.colors.white};
    border-top: 2px solid ${({ theme }) => theme.colors.gray.light};
    box-shadow: 0 -4px 20px ${({ theme }) => theme.colors.gray.medium}20;
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: ${({ theme }) => theme.spacing.gallery.sm} ${({ theme }) => theme.spacing.gallery.md};
    border-top: 1px solid ${({ theme }) => theme.colors.gallery.neutrals.light};
    background: ${({ theme }) => theme.colors.gallery.background}95;
    backdrop-filter: blur(6px);
  }
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily.gallery.body};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.gallery.hover};
  position: relative;
  overflow: hidden;
  min-height: 44px; /* Accessibility minimum touch target */

  ${({ theme, $variant }) => $variant === 'primary' ? css`
    background-color: ${theme.colors.primary};
    color: ${theme.colors.white};
    box-shadow: ${theme.shadows.gallery.soft};
    
    &:hover {
      background-color: ${theme.colors.secondary};
      transform: translateY(-1px);
      box-shadow: ${theme.shadows.gallery.comfortable};
    }
    
    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px ${theme.colors.primary}40;
    }
  ` : css`
    background-color: transparent;
    color: ${theme.colors.mobile.text};
    border: 1px solid ${theme.colors.mobile.border};
    
    &:hover {
      background-color: ${theme.colors.mobile.background};
      border-color: ${theme.colors.gray.dark};
    }
    
    &:focus {
      outline: none;
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 2px ${theme.colors.primary}30;
    }
  `}

  &:active {
    transform: translateY(1px);
  }

  /* Mobile-first responsive styling */
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing.mobile.md} ${({ theme }) => theme.spacing.mobile.lg};
    font-size: ${({ theme }) => theme.typography.fontSize.mobile.body};
    min-height: ${({ theme }) => theme.breakpoints.touchTarget};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.comfortable};
    font-weight: ${({ theme }) => theme.typography.fontWeight.gallery.semibold};
    width: 100%; /* Full width buttons on small mobile */
    
    /* Enhanced mobile touch feedback */
    &:active {
      transform: scale(0.98);
      transition: transform ${({ theme }) => theme.transitions.gallery.instant};
    }
    
    /* Primary button gets more emphasis on mobile */
    ${({ $variant }) => $variant === 'primary' && css`
      box-shadow: ${({ theme }) => theme.shadows.gallery.comfortable};
      
      &:hover {
        box-shadow: ${({ theme }) => theme.shadows.gallery.prominent};
      }
    `}
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    
    &:active {
      transform: scale(0.98);
      transition: transform 0.1s ease-out;
    }
    
    ${({ $variant }) => $variant === 'primary' && css`
      box-shadow: ${({ theme }) => theme.shadows.lg};
      
      &:hover {
        box-shadow: ${({ theme }) => theme.shadows.xl};
      }
    `}
  }
  
  /* Enhanced desktop styling */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    padding: ${({ theme }) => theme.spacing.gallery.tiny} ${({ theme }) => theme.spacing.gallery.sm};
    font-size: ${({ theme }) => theme.typography.fontSize.gallery.caption};
    border-radius: ${({ theme }) => theme.borderRadius.gallery.soft};
    
    ${({ $variant }) => $variant === 'primary' && css`
      box-shadow: ${({ theme }) => theme.shadows.gallery.artwork.subtle};
      
      &:hover {
        box-shadow: ${({ theme }) => theme.shadows.gallery.artwork.frame};
      }
    `}
  }
  
  /* Enhanced ripple effect for touch devices */
  @media (hover: none) and (pointer: coarse) {
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      border-radius: 50%;
      background: ${({ theme, $variant }) => 
        $variant === 'primary' ? theme.colors.white : theme.colors.mobile.focus}30;
      transform: translate(-50%, -50%);
      transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1), 
                  height 0.4s cubic-bezier(0.4, 0, 0.2, 1), 
                  opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      opacity: 0;
      pointer-events: none;
    }
    
    &:active::after {
      width: 120px;
      height: 120px;
      opacity: 0.4;
    }
  }
`;

export interface FileError {
  fileName: string;
  error: string;
}

export interface BulkUploadResult {
  totalFiles: number;
  successfulUploads: number;
  failedUploads: number;
  errors: FileError[];
}

export interface BulkUploadErrorSummaryProps {
  result: BulkUploadResult;
  onRetryFailed?: () => void;
  onDismiss?: () => void;
  onViewSuccessful?: () => void;
  /** Position the summary container - useful for mobile layouts */
  position?: 'fixed' | 'relative';
  /** Auto-scroll to component on mount - helpful for mobile */
  autoScroll?: boolean;
  /** Compact mode for smaller screens */
  compact?: boolean;
}

export function BulkUploadErrorSummary({
  result,
  onRetryFailed,
  onDismiss,
  onViewSuccessful,
  position = 'relative',
  autoScroll = false,
}: BulkUploadErrorSummaryProps) {
  const [expandedErrors, setExpandedErrors] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const { totalFiles, successfulUploads, failedUploads, errors } = result;
  const hasErrors = failedUploads > 0;
  const isSuccess = successfulUploads > 0;
  
  // Auto-scroll to component on mount if requested (helpful for mobile)
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 300); // Wait for animation to start
    }
  }, [autoScroll]);
  
  const getSummaryTitle = () => {
    if (isSuccess && !hasErrors) {
      return 'Upload Complete!';
    } else if (hasErrors && isSuccess) {
      return 'Partial Upload Success';
    } else {
      return 'Upload Failed';
    }
  };

  const getSummaryMessage = () => {
    if (isSuccess && !hasErrors) {
      return `All ${totalFiles} file(s) uploaded successfully.`;
    } else if (hasErrors && isSuccess) {
      return `${successfulUploads} of ${totalFiles} files uploaded successfully. ${failedUploads} files failed.`;
    } else {
      return `Failed to upload any files. ${failedUploads} error(s) occurred.`;
    }
  };

  return (
    <SummaryContainer ref={containerRef} $position={position}>
      <SummaryHeader $isSuccess={isSuccess} $hasErrors={hasErrors}>
        <SummaryTitle $isSuccess={isSuccess} $hasErrors={hasErrors}>
          {getSummaryTitle()}
        </SummaryTitle>
        
        <p style={{ 
          margin: 0, 
          color: 'inherit', 
          fontSize: '1rem',
          lineHeight: 1.5 
        }}>
          {getSummaryMessage()}
        </p>
        
        <SummaryStats>
          <StatItem $type="total">
            <div className="icon">T</div>
            <span className="label">Total Files:</span>
            <span className="count">{totalFiles}</span>
          </StatItem>
          
          {successfulUploads > 0 && (
            <StatItem $type="success">
              <div className="icon">✓</div>
              <span className="label">Successful:</span>
              <span className="count">{successfulUploads}</span>
            </StatItem>
          )}
          
          {failedUploads > 0 && (
            <StatItem $type="error">
              <div className="icon">✗</div>
              <span className="label">Failed:</span>
              <span className="count">{failedUploads}</span>
            </StatItem>
          )}
        </SummaryStats>
        
        {hasErrors && (
          <ToggleButton
            $expanded={expandedErrors}
            onClick={() => setExpandedErrors(!expandedErrors)}
            aria-expanded={expandedErrors}
            aria-controls="error-details"
          >
            {expandedErrors ? 'Hide' : 'Show'} Error Details
            <span className="chevron">▼</span>
          </ToggleButton>
        )}
      </SummaryHeader>
      
      {hasErrors && (
        <ErrorDetails $expanded={expandedErrors} id="error-details">
          <ErrorList>
            {errors.map((error, index) => (
              <ErrorItem key={index}>
                <FileName>{error.fileName}</FileName>
                <ErrorMessage>{error.error}</ErrorMessage>
              </ErrorItem>
            ))}
          </ErrorList>
        </ErrorDetails>
      )}
      
      <ActionButtons>
        {hasErrors && onRetryFailed && (
          <ActionButton 
            $variant="primary" 
            onClick={onRetryFailed}
            aria-label="Retry failed uploads"
          >
            Retry Failed Uploads
          </ActionButton>
        )}
        
        {isSuccess && onViewSuccessful && (
          <ActionButton 
            $variant="secondary" 
            onClick={onViewSuccessful}
            aria-label="View uploaded artwork"
          >
            View Uploaded Artwork
          </ActionButton>
        )}
        
        {onDismiss && (
          <ActionButton 
            $variant="secondary" 
            onClick={onDismiss}
            aria-label="Dismiss summary"
          >
            Dismiss
          </ActionButton>
        )}
      </ActionButtons>
    </SummaryContainer>
  );
}

export default BulkUploadErrorSummary;
